import type { Espace, Imputation } from '@mairistem/comptabilite';
import {
	FilterScope,
	type TemplateRequestGet,
} from '@mairistem/finances-utils';
import { request } from '@mairistem/tools';
import { chunk, isEmpty, uniq } from 'lodash';
import type Service from '../../interfaces/simulation/service';

const url = '/api/finances/comptabilite';

// 100 identifiants donnent une URL d'environ 700 caracteres
const TAILLE_LOT = 100;

export interface EspaceAvecService extends Espace {
	service?: Service;
}

export interface LigneEspace {
	espace?: EspaceAvecService;
	identifiant: number;
	identifiantEspace: number;
	identifiantImputation: number;
	imputation?: Imputation;
}

/**
 * Recupere des elements par identifiant avec le filtre identifiant=in(...) :
 * identifiants dedoublonnes, decoupes en lots, toutes les pages de chaque lot.
 * Un identifiant absent de la reponse est absent de la Map.
 */
export const fetchByIds = async <T extends { identifiant: number }>(
	route: string,
	ids: number[],
	extraParams: Record<string, string> = {},
): Promise<Map<number, T>> => {
	const elements = new Map<number, T>();
	if (isEmpty(ids)) return elements;

	await Promise.all(
		chunk(uniq(ids), TAILLE_LOT).map(async (lot) => {
			let page = 1;
			let lastPage = 1;
			do {
				const { data, meta } = await request.get<
					unknown,
					TemplateRequestGet<T>
				>(
					`${url}${route}?${new URLSearchParams({
						...extraParams,
						identifiant: FilterScope.filterIn(lot),
						page: String(page),
						// Sans perPage, le back pagine par 15
						perPage: String(lot.length),
					}).toString()}`,
				);
				data?.forEach((element) => {
					elements.set(element.identifiant, element);
				});
				lastPage = meta?.lastPage ?? 1;
				page += 1;
			} while (page <= lastPage);
		}),
	);

	return elements;
};

// Ne jamais passer withX=false : le back l'interprete comme true
export const getImputationsComptabilite = (ids: number[]) =>
	fetchByIds<Imputation>('/imputations', ids, {
		withAnalytique: 'true',
		withArticle: 'true',
		withChapitre: 'true',
		withFonction: 'true',
		withOperation: 'true',
		withVentilation: 'true',
	});

export const getEspaces = (ids: number[]) =>
	fetchByIds<Espace>('/imputations/espaces', ids);

// L'imputation et l'espace (avec son service) sont inclus dans chaque ligne
export const getLignesEspace = (ids: number[]) =>
	fetchByIds<LigneEspace>('/imputations/ligneEspaces', ids, {
		withEspace: 'true',
		withImputation: 'true',
	});
